import { currencyMap } from '../../../../../Constants'
import {
  eventDateAndTimeFormatting,
  eventSubmissionFormatting,
} from '../../../../../Utilities/useTimeSinceNow'
import {
  EventInformationFragment,
  SpaceStyleFragment,
} from '../../Drawer.generated'

const getCurrencySymbol = (
  currency: EventInformationFragment['currency']
): string =>
  currency && currencyMap[currency] ? currencyMap[currency] : currencyMap.GBP

interface FormatLocationArgs
  extends Pick<EventInformationFragment, 'location' | 'radius'> {
  isDirectEnquiry: boolean
}

export const formatLocation = ({
  radius,
  isDirectEnquiry,
  location,
}: FormatLocationArgs): string | null => {
  if (!location) return null
  return `${location}${!isDirectEnquiry ? ` + ${radius} km` : ''}`
}

export const formatBudget = ({
  currency,
  budget,
}: Pick<EventInformationFragment, 'currency' | 'budget'>): string | null => {
  if (!budget) return null
  return `${getCurrencySymbol(currency)}${budget / 100}`
}

export const formatAttendees = ({
  guestsCount,
  layout,
}: Pick<EventInformationFragment, 'guestsCount' | 'layout'>): string | null => {
  if (!guestsCount) return null
  return `${guestsCount} ${
    !layout || layout === 'not_sure' ? 'guests' : layout
  }`
}

export const formatTimings = ({
  eventDateTo,
  eventDateFrom,
}: Pick<EventInformationFragment, 'eventDateTo' | 'eventDateFrom'>):
  | string
  | null => {
  if (!eventDateFrom || !eventDateTo) return null
  return eventDateAndTimeFormatting(eventDateFrom, eventDateTo)
}

type FormatFlexibilityArgs = EventInformationFragment['flexibility'] & {
  legacyFlexibleDates?: boolean
}

export const formatFlexibility = ({
  legacyFlexibleDates,
  flexibleDates,
  flexibleTimes,
  notes,
}: FormatFlexibilityArgs): string | null => {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
  const appendage = notes ? `: "${notes}"` : ''

  if (!legacyFlexibleDates && !flexibleDates && !flexibleTimes) return null

  if (legacyFlexibleDates) return `Flexible with date or time${appendage}`

  if (flexibleDates && flexibleTimes)
    return `Flexible with date and time${appendage}`

  if (flexibleDates) return `Flexible with date${appendage}`

  return `Flexible with time${appendage}`
}

export const formatStyles = (
  spaceStyles?: SpaceStyleFragment[] | null
): SpaceStyleFragment[] => {
  if (!spaceStyles || !spaceStyles.length) return []
  return [...spaceStyles]
    .filter((space) => !!space.id && !!space.label)
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const formatContactDetails = (
  guest: EventInformationFragment['guest']
): string | null => {
  if (!guest) return null
  const { email, phoneNumber, mobileNumber } = guest
  if (!email) return null
  return `${email}, ${phoneNumber ?? mobileNumber ?? ''}`
}

export const formatQuote = (text?: string | null): string | null =>
  text ? `"${text}"` : null

export const formatSubmissionDate = (
  createDate: EventInformationFragment['createDate']
): string | null => {
  if (!createDate) return null
  return eventSubmissionFormatting(createDate)
}
